import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../theme/phone-plan';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { UserAvatar } from './user-avatar';
import { usePlan } from './plan-context';
import { useLayer } from './layer-stack';
import { NavIcon } from './nav-icon';
import { SignOutLabel } from './sign-out-progress';
import type { NavIconName } from './nav-icon';
import { LanguageToggle } from './language-toggle';
import { ThemeToggle } from './theme-toggle';
import { PauseConfirmDialog } from './pause-confirm-dialog';
import { AccountSwitcher } from './account-switcher';
import type { MessageKey } from '@bookingdei/i18n/message-key';

// The top-bar user menu (design M:128-156 + userMenuItems M:12340-12350; CB-17
// `account-menu.tsx`): photo or initial + name + plan line (or TEAM for a staff session) →
// Account · My Plan · Help · Contact · Log out. It also carries the "Pause online bookings"
// switch until My Business owns it (DC76) — pausing is confirmed first, resuming is
// instant — and, below 900 px where the top bar has no room, the language and theme
// toggles. The M32 "Continue as…" role switcher (moved from the old rail foot)
// shows only for multi-role accounts.
export function AccountMenu({
  open,
  onOpenChange,
  isStaff,
  onHelp,
  onContact,
  onLogout,
  loggingOut,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isStaff: boolean;
  onHelp: () => void;
  onContact: () => void;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const { lang, t } = useLanguage();
  const { user, activeBusiness, setOnlinePaused } = useSession();
  const { subscription } = usePlan();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [confirmPause, setConfirmPause] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);

  useLayer(open, { kind: 'popover', onDismiss: () => onOpenChange(false), containerRef: panelRef });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onOpenChange(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onOpenChange]);

  const name = user?.name ?? '';
  const plan = subscription?.plan;
  const planName = plan ? (lang === 'bn' && plan.name_bn ? plan.name_bn : plan.name_en) : '';
  const roleLine = isStaff ? t('dc70.role.team') : planName;
  const paused = Boolean(activeBusiness?.online_paused);

  const goTo = (to: string) => () => {
    onOpenChange(false);
    navigate(to);
  };

  const setPause = async (next: boolean) => {
    setPauseBusy(true);
    try {
      await setOnlinePaused(next);
      setConfirmPause(false);
    } finally {
      setPauseBusy(false);
    }
  };

  const items: { key: MessageKey; icon: NavIconName; run: () => void; danger?: boolean }[] = [
    { key: 'dc70.nav.account', icon: 'person', run: goTo('/account') },
    ...(isStaff
      ? []
      : [{ key: 'dc70.nav.plan' as MessageKey, icon: 'taka' as NavIconName, run: goTo('/plan') }]),
    {
      key: 'dc70.help.open',
      icon: 'help',
      run: () => {
        onOpenChange(false);
        onHelp();
      },
    },
    {
      // Below 900 px the rail (and its Contact button) is hidden, so the menu carries it.
      key: 'dc70.help.contact',
      icon: 'inbox',
      run: () => {
        onOpenChange(false);
        onContact();
      },
    },
  ];

  return (
    <div className="acct" ref={wrapRef}>
      <button
        type="button"
        className="acct__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('dc70.top.user_menu')}
        onClick={() => onOpenChange(!open)}
        data-shell="user-menu"
      >
        <UserAvatar src={user?.avatar_url} name={name} className="acct__avatar" imgClassName="acct__avatar acct__avatar--img" />
        <span className="acct__who">
          <span className="acct__name">{name}</span>
          {roleLine && <span className="acct__role">{roleLine}</span>}
        </span>
        <span className="acct__caret" aria-hidden="true">
          <NavIcon name="chevron" size={14} strokeWidth={2.4} />
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="acct__menu"
          role="menu"
          aria-label={t('dc70.top.user_menu')}
          tabIndex={-1}
        >
          <div className="acct__head">
            <UserAvatar
              src={user?.avatar_url}
              name={name}
              className="acct__avatar acct__avatar--lg"
              imgClassName="acct__avatar acct__avatar--lg acct__avatar--img"
            />
            <span className="acct__head-text">
              <span className="acct__head-name">{name}</span>
              <span className="acct__head-mail">{user?.email ?? formatPhone(user?.phone, phonePlan)}</span>
            </span>
          </div>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              className="acct__item"
              onClick={it.run}
              data-menu={it.key}
            >
              <NavIcon name={it.icon} size={17} strokeWidth={1.9} />
              <span>{t(it.key)}</span>
            </button>
          ))}
          {!isStaff && activeBusiness && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={paused}
              className={`acct__item acct__pause${paused ? ' is-on' : ''}`}
              disabled={pauseBusy}
              onClick={() => (paused ? setPause(false) : setConfirmPause(true))}
            >
              <span className="acct__switch" aria-hidden="true">
                <span className="acct__knob" />
              </span>
              <span>{t('merchant.pause_online')}</span>
            </button>
          )}
          {/* DC123: a menu may only own menu items; the preference toggles and the role
              switcher sit in named groups (axe aria-required-children). */}
          <div
            className="acct__prefs"
            role="group"
            aria-label={`${t('dc70.menu.language')} · ${t('dc70.menu.theme')}`}
          >
            <span className="acct__prefs-label">{t('dc70.menu.language')}</span>
            <LanguageToggle menuItem />
            <span className="acct__prefs-label">{t('dc70.menu.theme')}</span>
            <ThemeToggle menuItem />
          </div>
          <div className="acct__switcher" role="group" aria-label={t('user.account.switch_heading')}>
            <AccountSwitcher />
          </div>
          <button
            type="button"
            role="menuitem"
            className="acct__item acct__item--danger"
            disabled={loggingOut}
            aria-busy={loggingOut}
            onClick={() => {
              onOpenChange(false);
              onLogout();
            }}
          >
            <SignOutLabel label={t('dc70.help.logout')} icon={<NavIcon name="logout" size={17} strokeWidth={1.9} />} />
          </button>
        </div>
      )}

      {confirmPause && (
        <PauseConfirmDialog
          busy={pauseBusy}
          onKeep={() => setConfirmPause(false)}
          onConfirm={() => setPause(true)}
        />
      )}
    </div>
  );
}
